import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectLoader } from '../../../reducers/documentation/salesforceDocumentationReducer';
import { DocumentationCardItem } from '../salesforce-documentation-card/types';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useUniversalSearchContext } from './UniversalSearchContext';
import { SearchResponseWithConfigurationItems, SearchType } from './types';
import flatten from 'lodash/flatten';

interface UseUniversalSearchProps {
  crmOrgId: string;
  onItemClick?: (
    props: DocumentationCardItem,
    results?: SearchResponseWithConfigurationItems,
  ) => Promise<void>;
  onClearClick?: () => void;
  onBiEventSearchStarted?: (props: { searchQuery: string; searchType: string }) => void;
  onBiEventSearchResultsPresented?: (props: {
    searchQuery: string;
    numberOfResults: number;
  }) => void;
}

export const useUniversalSearch = ({
  crmOrgId,
  onItemClick,
  onClearClick,
  onBiEventSearchStarted,
  onBiEventSearchResultsPresented,
}: UseUniversalSearchProps) => {
  const context = useUniversalSearchContext();
  const {
    searchTxt,
    tempSearchTxt,
    setTempSearchTxt,
    isLoading,
    results,
    performSearch,
    onSearchChange,
    onClearButtonClick: baseClearButtonClick,
    selectedSearchType,
    onSelectSearchType,
    filterOptions,
    filterKey,
    onSelectedFilterItem,
    searchTypeOptions,
    isSearchListOpen,
    setIsSearchListOpen,
  } = context;

  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const showLoader = useSelector(selectLoader(crmOrgId));

  const onClearButtonClick = useCallback(() => {
    baseClearButtonClick();
    setIsSearchListOpen(false);
    onClearClick?.();
  }, [baseClearButtonClick, setIsSearchListOpen, onClearClick]);

  const performSearchProgrammatically = useCallback(
    async (searchText: string, _selectedSearchType?: string) => {
      if (!searchText.trim()) {
        onClearButtonClick();
        return;
      }

      const searchType = _selectedSearchType ?? selectedSearchType;
      const biSearchType = searchType === SearchType.content ? 'byContent' : 'byName';
      onBiEventSearchStarted?.({ searchQuery: searchText, searchType: biSearchType });

      setTempSearchTxt(searchText);
      const searchResults = await performSearch(searchText, searchType);

      if (searchResults) {
        const numberOfResults = flatten(Object.values(searchResults ?? {})).length;
        onBiEventSearchResultsPresented?.({ searchQuery: searchText, numberOfResults });
      }
    },
    [
      selectedSearchType,
      onClearButtonClick,
      performSearch,
      setTempSearchTxt,
      onBiEventSearchStarted,
      onBiEventSearchResultsPresented,
    ],
  );

  const onSearchKeydown = useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>, _selectedSearchType?: SearchType) => {
      if (event.key === 'Escape') {
        onClearButtonClick();
        return;
      }
      if (event.key === 'Enter') {
        await performSearchProgrammatically(tempSearchTxt, _selectedSearchType);
        return;
      }
    },
    [tempSearchTxt, onClearButtonClick, performSearchProgrammatically],
  );

  const onSelectSearchTypeOption = useCallback(
    async (value: string) => {
      const biSearchType = value === SearchType.content ? 'byContent' : 'byName';
      onBiEventSearchStarted?.({ searchQuery: searchTxt, searchType: biSearchType });

      const searchResults = await onSelectSearchType(value);

      if (searchResults) {
        const numberOfResults = flatten(Object.values(searchResults ?? {})).length;
        onBiEventSearchResultsPresented?.({ searchQuery: searchTxt, numberOfResults });
      }
    },
    [searchTxt, onSelectSearchType, onBiEventSearchStarted, onBiEventSearchResultsPresented],
  );

  const onListItemClick = useCallback(
    async (props: DocumentationCardItem) => {
      await onItemClick?.(props, results);
    },
    [onItemClick, results],
  );

  return {
    searchTxt,
    tempSearchTxt,
    isLoadingObjects: !saltoObjectTypes?.length || showLoader,
    isSearchListOpen,
    isLoadingResults: isLoading,
    results,
    filterOptions,
    selectedFilterValue: filterKey,
    searchTypeOptions,
    selectedSearchType,
    onSearchKeydown,
    performSearchProgrammatically,
    onClearButtonClick,
    onSearchChange,
    onSelectedFilterItem,
    onListItemClick,
    onSelectSearchTypeOption,
    setIsSearchListOpen,
  };
};
